import React, { useEffect, useState, useCallback } from "react";

// reactstrap components
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import { useToasts } from "react-toast-notifications";
import { postMethod } from "services/httpServices";
import { getAmt } from "services/util";
import moment from "moment";
import { subsMTDWalletTrans } from "variables/charts";
import { daysInMonth, daysInArray } from "services/util";

const AdminHeader = (props) => {
  // New dashboard metrics state
  const [dashboardData, setDashboardData] = useState({});
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailData, setDetailData] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    key: "CreatedAt",
    direction: "desc",
  });

  const { addToast } = useToasts();

  useEffect(() => {
    fetchDashboardSummary();
  }, []);

  const notifyFailed = (text) => {
    if (!text) {
      text = "Error in saving";
    }
    addToast(text, {
      appearance: "error",
      autoDismiss: true,
    });
  };

  // New dashboard functions
  const fetchDashboardSummary = useCallback(async () => {
    setDashboardLoading(true);
    try {
      const response = await postMethod(
        "/api/Dashboard/GetDashboardSummary",
        {}
      );

      if (response && response.data) {
        setDashboardData(response.data);
      } else {
        addToast("Failed to fetch dashboard summary", {
          appearance: "error",
        });
      }
    } catch (error) {
      addToast("Error fetching dashboard summary", {
        appearance: "error",
      });
    } finally {
      setDashboardLoading(false);
    }
  }, [addToast]);

  const fetchDetailData = useCallback(
    async (filterType, page = 1) => {
      setDetailLoading(true);
      try {
        const payload = {
          searchText: "",
          pageNumber: page,
          pageSize: 20,
          sortingOn: "CreatedAt",
          isDescending: true,
        };

        const response = await postMethod(
          `/api/Dashboard/GetDashboardDetails/${filterType}`,
          payload,
          "post"
        );

        if (response && response.data) {
          // API returns array directly, not wrapped in results
          setDetailData(Array.isArray(response.data) ? response.data : []);
          // Since API returns all data, we'll handle pagination client-side
          setTotalPages(
            Math.ceil(
              (Array.isArray(response.data) ? response.data : []).length / 20
            )
          );
          setCurrentPage(page);
        } else {
          addToast("Failed to fetch detail data", {
            appearance: "error",
          });
        }
      } catch (error) {
        addToast("Error fetching detail data", {
          appearance: "error",
        });
      } finally {
        setDetailLoading(false);
      }
    },
    [addToast]
  );

  const handleMetricClick = (metricKey, filterType) => {
    setSelectedMetric({ key: metricKey, filterType });
    setShowDetailModal(true);
    fetchDetailData(filterType);
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const closeModal = () => {
    setShowDetailModal(false);
    setSelectedMetric(null);
    setDetailData([]);
    setCurrentPage(1);
    setTotalPages(0);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <i className="fas fa-sort text-muted" />;
    }
    return sortConfig.direction === "asc" ? (
      <i className="fas fa-sort-up text-primary" />
    ) : (
      <i className="fas fa-sort-down text-primary" />
    );
  };

  const getMetricConfig = () => {
    return [
      {
        key: "totalWalletBalance",
        title: "Total Wallet Balance",
        value: dashboardData.totalWalletBalance || 0,
        icon: "fas fa-wallet",
        color: "primary",
        filterType: "TotalWalletBalance",
      },
      {
        key: "lapoPayWalletBalance",
        title: "Lapo Pay Wallet Balance",
        value: dashboardData.lapoPayWalletBalance || 0,
        icon: "fas fa-credit-card",
        color: "info",
        filterType: "LapoPayWalletBalance",
      },
      {
        key: "totalCashIn",
        title: "Cash In",
        value: dashboardData.totalCashIn || 0,
        icon: "fas fa-arrow-down",
        color: "success",
        filterType: "CashIn",
      },
      {
        key: "totalCashOut",
        title: "Cash Out",
        value: dashboardData.totalCashOut || 0,
        icon: "fas fa-arrow-up",
        color: "danger",
        filterType: "CashOut",
      },
      {
        key: "totalWalletTransfers",
        title: "Wallet Transfer",
        value: dashboardData.totalWalletTransfers || 0,
        icon: "fas fa-exchange-alt",
        color: "warning",
        filterType: "WalletTransfer",
      },
      {
        key: "totalBankToWallet",
        title: "Bank Withdrawal",
        value: dashboardData.totalBankToWallet || 0,
        icon: "fas fa-university",
        color: "primary",
        filterType: "BankWithdrawal",
      },
      {
        key: "totalWalletToBank",
        title: "Bank Transfer",
        value: dashboardData.totalWalletToBank || 0,
        icon: "fas fa-exchange-alt",
        color: "warning",
        filterType: "BankTransfer",
      },
      {
        key: "totalLoanRepayments",
        title: "Loan Payment",
        value: dashboardData.totalLoanRepayments || 0,
        icon: "fas fa-hand-holding-usd",
        color: "success",
        filterType: "LoanPayment",
      },
      {
        key: "totalCharges",
        title: "Charges",
        value: dashboardData.totalCharges || 0,
        icon: "fas fa-receipt",
        color: "dark",
        filterType: "Charges",
      },
    ];
  };

  const getTableColumns = (filterType) => {
    // Define column mappings based on the provided format
    const columnMappings = {
      // Wallet-based columns
      TotalWalletBalance: [
        { key: "fullName", label: "Full Name" },
        { key: "mobileNumber", label: "Mobile Number" },
        { key: "email", label: "Email" },
        { key: "balanceAmount", label: "Balance Amount" },
        { key: "createdAt", label: "Created At" },
      ],
      LapoPayWalletBalance: [
        { key: "fullName", label: "Full Name" },
        { key: "mobileNumber", label: "Mobile Number" },
        { key: "email", label: "Email" },
        { key: "balanceAmount", label: "Balance Amount" },
        { key: "createdAt", label: "Created At" },
      ],
      // Transaction-based columns
      CashIn: [
        { key: "transactionId", label: "Transaction ID" },
        { key: "paymentFrom", label: "Payment From" },
        { key: "paymentTo", label: "Payment To" },
        { key: "amount", label: "Amount" },
        { key: "description", label: "Description" },
        { key: "createdAt", label: "Created At" },
      ],
      CashOut: [
        { key: "transactionId", label: "Transaction ID" },
        { key: "paymentFrom", label: "Payment From" },
        { key: "paymentTo", label: "Payment To" },
        { key: "amount", label: "Amount" },
        { key: "description", label: "Description" },
        { key: "createdAt", label: "Created At" },
      ],
      WalletTransfer: [
        { key: "transactionId", label: "Transaction ID" },
        { key: "paymentFrom", label: "Payment From" },
        { key: "paymentTo", label: "Payment To" },
        { key: "amount", label: "Amount" },
        { key: "createdAt", label: "Created At" },
      ],
      BankWithdrawal: [
        { key: "transactionId", label: "Transaction ID" },
        { key: "paymentFrom", label: "Payment From" },
        { key: "paymentTo", label: "Payment To" },
        { key: "amount", label: "Amount" },
        { key: "createdAt", label: "Created At" },
      ],
      BankTransfer: [
        { key: "transactionId", label: "Transaction ID" },
        { key: "paymentFrom", label: "Payment From" },
        { key: "paymentTo", label: "Payment To" },
        { key: "amount", label: "Amount" },
        { key: "createdAt", label: "Created At" },
      ],
      LoanPayment: [
        { key: "transactionId", label: "Transaction ID" },
        { key: "paymentFrom", label: "Payment From" },
        { key: "paymentTo", label: "Payment To" },
        { key: "amount", label: "Amount" },
        { key: "createdAt", label: "Created At" },
      ],
      Charges: [
        { key: "transactionId", label: "Transaction ID" },
        { key: "paymentFrom", label: "Payment From" },
        { key: "paymentTo", label: "Payment To" },
        { key: "amount", label: "Amount" },
        { key: "description", label: "Description" },
        { key: "createdAt", label: "Created At" },
      ],
    };

    // Return the specific columns for the filter type, or default transaction columns
    return (
      columnMappings[filterType] || [
        { key: "transactionId", label: "Transaction ID" },
        { key: "paymentFrom", label: "Payment From" },
        { key: "paymentTo", label: "Payment To" },
        { key: "amount", label: "Amount" },
        { key: "createdAt", label: "Created At" },
      ]
    );
  };

  const sortData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle dates
      if (sortConfig.key === "createdAt") {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        return sortConfig.direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      // Handle numbers
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      // Handle strings
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return sortConfig.direction === "asc"
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  };

  const renderTableData = (data, columns) => {
    const sortedData = sortData(data);
    return sortedData.map((item, index) => (
      <tr key={index}>
        {columns.map((column) => (
          <td key={column.key} className="align-middle">
            {column.key === "createdAt"
              ? moment(item[column.key]).format("DD/MM/YYYY HH:mm")
              : column.key === "amount" ||
                column.key === "balanceAmount" ||
                column.key === "feesAmount" ||
                column.key === "merchantFeesAmount"
              ? getAmt(item[column.key])
              : item[column.key] || "-"}
          </td>
        ))}
      </tr>
    ));
  };

  const getAmt = (amt) => {
    return amt?.toLocaleString();
  };

  /**========================= MTD Wallet Transfer ===================================== */

  useEffect(() => {
    if (props.showAdminMTDWalletTransLoader) {
      props.showAdminMTDWalletTransLoader(true);
      setAdminMTDWalletTrans();
    }
  }, [props.adminMTDWalletTransMonth]);

  useEffect(() => {
    if (props.showAdminMTDWalletTransLoader) {
      props.showAdminMTDWalletTransLoader(true);
      setAdminMTDWalletTrans();
    }
  }, [props.adminMTDWalletTransYear]);

  useEffect(() => {
    if (props.showSubAgentMTDWalletTransLoader) {
      props.showSubAgentMTDWalletTransLoader(true);
      setSubAgentMTDWalletTrans();
    }
  }, [props.subAgentMTDWalletTransMonth]);

  useEffect(() => {
    if (props.showSubAgentMTDWalletTransLoader) {
      props.showSubAgentMTDWalletTransLoader(true);
      setSubAgentMTDWalletTrans();
    }
  }, [props.subAgentMTDWalletTransYear]);

  const setAdminMTDWalletTrans = () => {
    if (!props.adminMTDWalletTransYear || !props.adminMTDWalletTransMonth) {
      return;
    }
    var errorMsg = "";
    var data = {
      Year: Number(props.adminMTDWalletTransYear),
      Month: Number(props.adminMTDWalletTransMonth) + 1,
      Status: "Paid",
    };
    if (!data.Month || !data.Year) {
      return;
    }
    postMethod("/api/dashboard/MonthlyCustomerCashTopup", data, "post")
      .then((res) => {
        if (res && res.data) {
          if (
            res.data.responseCode === "200" &&
            res.data.result &&
            res.data.result.list
          ) {
            // Success
            let month = Number(props.adminMTDWalletTransMonth);
            let year = Number(props.adminMTDWalletTransYear);
            let totalDays = daysInMonth(month + 1, year);
            let arr = daysInArray(totalDays);
            let sendArr = [];
            let getArr = [];

            for (let i = 0; i < arr.length; i++) {
              let val = res.data.result.list.find((l) => l.day == arr[i]);
              if (val && val.hasOwnProperty("transferFrom")) {
                getArr.push(Number(val.transferFrom));
              } else {
                getArr.push(0);
              }
              if (val && val.hasOwnProperty("transferTo")) {
                sendArr.push(Number(val.transferTo));
              } else {
                sendArr.push(0);
              }
            }
            let mtdChart = subsMTDWalletTrans({
              daysArr: arr,
              getArr: getArr,
              sendArr: sendArr,
            });
            if (props.showAdminMTDWalletTrans) {
              props.showAdminMTDWalletTrans(mtdChart);
            }
            if (props.showAdminMTDWalletTransLoader) {
              props.showAdminMTDWalletTransLoader(false);
            }
          } else if (res.data.responseCode === "400") {
            // Error
            errorMsg = res.data.responseMessage || "Error in fetching charts";
            notifyFailed(errorMsg);
            if (props.showAdminMTDWalletTransLoader) {
              props.showAdminMTDWalletTransLoader(false);
            }
          } else {
            // Unknown Error
            errorMsg = "Unknown Error in fetching charts";
            notifyFailed(errorMsg);
            if (props.showAdminMTDWalletTransLoader) {
              props.showAdminMTDWalletTransLoader(false);
            }
          }
        } else {
          errorMsg = "Unknown Error";
          notifyFailed(errorMsg);
          if (props.showAdminMTDWalletTransLoader) {
            props.showAdminMTDWalletTransLoader(false);
          }
        }
      })
      .catch((err) => {
        notifyFailed("Internal Server Error");
        if (props.showAdminMTDWalletTransLoader) {
          props.showAdminMTDWalletTransLoader(false);
        }
      });
  };

  const setSubAgentMTDWalletTrans = () => {
    if (
      !props.subAgentMTDWalletTransYear ||
      !props.subAgentMTDWalletTransMonth
    ) {
      return;
    }
    var errorMsg = "";
    var data = {
      Year: Number(props.subAgentMTDWalletTransYear),
      Month: Number(props.subAgentMTDWalletTransMonth) + 1,
      Status: "Paid",
    };
    if (!data.Month || !data.Year) {
      return;
    }
    postMethod("/api/dashboard/MonthlySubAgentCommissionMTD", data, "post")
      .then((res) => {
        if (res && res.data) {
          if (
            res.data.responseCode === "200" &&
            res.data.result &&
            res.data.result.list
          ) {
            // Success
            let month = Number(props.subAgentMTDWalletTransMonth);
            let year = Number(props.subAgentMTDWalletTransYear);
            let totalDays = daysInMonth(month + 1, year);
            let arr = daysInArray(totalDays);
            let sendArr = [];
            let getArr = [];

            for (let i = 0; i < arr.length; i++) {
              let val = res.data.result.list.find((l) => l.day == arr[i]);
              if (val && val.hasOwnProperty("transferFrom")) {
                getArr.push(Number(val.transferFrom));
              } else {
                getArr.push(0);
              }
              if (val && val.hasOwnProperty("transferTo")) {
                sendArr.push(Number(val.transferTo));
              } else {
                sendArr.push(0);
              }
            }
            let mtdChart = subsMTDWalletTrans({
              daysArr: arr,
              getArr: getArr,
              sendArr: sendArr,
            });
            if (props.showSubAgentMTDWalletTrans) {
              props.showSubAgentMTDWalletTrans(mtdChart);
            }
            if (props.showSubAgentMTDWalletTransLoader) {
              props.showSubAgentMTDWalletTransLoader(false);
            }
          } else if (res.data.responseCode === "400") {
            // Error
            errorMsg = res.data.responseMessage || "Error in fetching charts";
            notifyFailed(errorMsg);
            if (props.showSubAgentMTDWalletTransLoader) {
              props.showSubAgentMTDWalletTransLoader(false);
            }
          } else {
            // Unknown Error
            errorMsg = "Unknown Error in fetching charts";
            notifyFailed(errorMsg);
            if (props.showSubAgentMTDWalletTransLoader) {
              props.showSubAgentMTDWalletTransLoader(false);
            }
          }
        } else {
          errorMsg = "Unknown Error";
          notifyFailed(errorMsg);
          if (props.showSubAgentMTDWalletTransLoader) {
            props.showSubAgentMTDWalletTransLoader(false);
          }
        }
      })
      .catch((err) => {
        notifyFailed("Internal Server Error");
        if (props.showSubAgentMTDWalletTransLoader) {
          props.showSubAgentMTDWalletTransLoader(false);
        }
      });
  };

  return (
    <>
      <style>{`
        .metric-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
          opacity: 0.9;
        }
        .metric-card {
          transition: all 0.3s ease;
        }
        .loading-dots {
          display: inline-flex;
          align-items: center;
          gap: 3px;
        }
        .loading-dots span {
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background-color: #007bff;
          animation: loading-dots 1.4s infinite ease-in-out both;
        }
        .loading-dots span:nth-child(1) {
          animation-delay: -0.32s;
        }
        .loading-dots span:nth-child(2) {
          animation-delay: -0.16s;
        }
        .loading-dots-white {
          display: inline-flex;
          align-items: center;
          gap: 3px;
        }
        .loading-dots-white span {
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background-color: white;
          animation: loading-dots 1.4s infinite ease-in-out both;
        }
        .loading-dots-white span:nth-child(1) {
          animation-delay: -0.32s;
        }
        .loading-dots-white span:nth-child(2) {
          animation-delay: -0.16s;
        }
        @keyframes loading-dots {
          0%, 80%, 100% {
            transform: scale(0);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
        .modal-close-btn {
          transition: all 0.2s ease;
        }
        .modal-close-btn:hover {
          color: #dc3545 !important;
          transform: scale(1.1);
        }
      
        .modal-pagination {
          background: #f8f9fa;
          border-top: 1px solid #dee2e6;
          margin: 0 -1.5rem -1rem -1.5rem;
          padding: 1rem 1.5rem;
          border-radius: 0 0 0.375rem 0.375rem;
        }
        .pagination-info {
          color: #6c757d;
          font-size: 0.875rem;
          font-weight: 500;
        }
        .pagination-buttons {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        .pagination-buttons .d-flex {
          gap: 0.5rem;
        }
        .page-selector {
          min-width: 60px;
          max-width: 70px;
          padding: 0.25rem 0.5rem;
          font-size: 0.875rem;
          border: 1px solid #ced4da;
          border-radius: 0.25rem;
          background-color: white;
        }
        .page-selector:focus {
          border-color: #80bdff;
          outline: 0;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }
        .pagination-btn {
          min-width: 40px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.875rem;
          border-radius: 0.25rem;
        }
        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        @media (max-width: 768px) {
          .modal-pagination {
            padding: 0.75rem 1rem;
          }
          .pagination-buttons {
            justify-content: center;
            margin-top: 0.5rem;
          }
        }
      `}</style>
      <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body">
            {/* Card stats */}

            <Row>
              {getMetricConfig().map((metric) => (
                <Col lg="4" xl="4" key={metric.key} className="mb-4">
                  <Card
                    className="card-stats mb-4 mb-xl-0 metric-card"
                    style={{
                      cursor: dashboardLoading ? "default" : "pointer",
                      transition: "all 0.3s ease",
                      border: "1px solid #e9ecef",
                      opacity: dashboardLoading ? 0.7 : 1,
                    }}
                    onClick={() =>
                      !dashboardLoading &&
                      handleMetricClick(metric.key, metric.filterType)
                    }
                  >
                    <CardBody>
                      <Row>
                        <div className="col">
                          <h5 className="card-title text-uppercase text-muted mb-0">
                            {metric.title}
                          </h5>
                          {dashboardLoading ? (
                            <div className="d-flex align-items-center">
                              <div className="loading-dots">
                                <span></span>
                                <span></span>
                                <span></span>
                              </div>
                            </div>
                          ) : (
                            <span className="h2 font-weight-bold mb-0">
                              {getAmt(metric.value)}
                            </span>
                          )}
                        </div>
                        <Col className="col-auto">
                          <div
                            className={`icon icon-shape bg-${metric.color} text-white rounded-circle shadow`}
                          >
                            {dashboardLoading ? (
                              <div className="loading-dots-white">
                                <span></span>
                                <span></span>
                                <span></span>
                              </div>
                            ) : (
                              <i className={metric.icon} />
                            )}
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </Container>
      </div>

      {/* Detail Modal for Dashboard Metrics */}
      <Modal isOpen={showDetailModal} toggle={closeModal} size="xl">
        <ModalHeader toggle={closeModal} className="bg-white">
          <h4 className="text-white">
            {selectedMetric
              ? `${
                  getMetricConfig().find((m) => m.key === selectedMetric.key)
                    ?.title
                } Details`
              : "Details"}
          </h4>
        </ModalHeader>
        <ModalBody>
          {detailLoading ? (
            <div className="text-center py-4">
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table className="align-items-center table-flush" responsive>
                  <thead className="thead-light">
                    <tr>
                      {selectedMetric
                        ? getTableColumns(selectedMetric.filterType).map(
                            (column) => (
                              <th
                                key={column.key}
                                className="sort"
                                data-sort={column.key}
                                style={{ cursor: "pointer" }}
                                onClick={() => handleSort(column.key)}
                              >
                                <div className="d-flex align-items-center">
                                  <span>{column.label}</span>
                                  {getSortIcon(column.key)}
                                </div>
                              </th>
                            )
                          )
                        : null}
                    </tr>
                  </thead>
                  <tbody>
                    {detailData.length > 0 ? (
                      renderTableData(
                        detailData.slice(
                          (currentPage - 1) * 20,
                          currentPage * 20
                        ),
                        selectedMetric
                          ? getTableColumns(selectedMetric.filterType)
                          : []
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={
                            selectedMetric
                              ? getTableColumns(selectedMetric.filterType)
                                  .length
                              : 1
                          }
                          className="text-center"
                        >
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {detailData.length > 0 && (
                <div className="modal-pagination">
                  <div className="d-flex justify-content-between align-items-center flex-wrap">
                    <div className="pagination-info">
                      {totalPages > 1
                        ? `Showing ${(currentPage - 1) * 20 + 1} to ${Math.min(
                            currentPage * 20,
                            detailData.length
                          )} of ${detailData.length} items`
                        : `Showing ${detailData.length} item${
                            detailData.length !== 1 ? "s" : ""
                          }`}
                    </div>
                    {totalPages > 1 && (
                      <div className="pagination-buttons">
                        <Button
                          color="primary"
                          size="sm"
                          disabled={currentPage === 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                          className="pagination-btn"
                        >
                          <i className="fas fa-chevron-left"></i>
                          <span className="d-none d-sm-inline ms-1">Prev</span>
                        </Button>

                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted small">Page</span>
                          <select
                            className="page-selector"
                            value={currentPage}
                            onChange={(e) =>
                              handlePageChange(parseInt(e.target.value))
                            }
                          >
                            {[...Array(totalPages)].map((_, index) => (
                              <option key={index + 1} value={index + 1}>
                                {index + 1}
                              </option>
                            ))}
                          </select>
                          <span className="text-muted small">
                            of {totalPages}
                          </span>
                        </div>

                        <Button
                          color="primary"
                          size="sm"
                          disabled={currentPage === totalPages}
                          onClick={() => handlePageChange(currentPage + 1)}
                          className="pagination-btn"
                        >
                          <span className="d-none d-sm-inline me-1">Next</span>
                          <i className="fas fa-chevron-right"></i>
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default AdminHeader;
